<template>
  <div
    class="confirmationContainer"
    :style="{ height: isQuickStart ? 'calc(100vh - 48px)' : '100vh' }"
    :class="{ 'mt-12': isQuickStart }"
  >
    <v-row
      class="confirmationContent justify-space-between"
      :class="{ mobileConfirmationContent: isMobileSized }"
    >
      <v-col
        :cols="isMobileSized ? 12 : 6"
        class="d-flex px-0"
        style="position:relative;"
        :class="{ 'mt-16': isMobileSized }"
        :style="isMobileSized ? '' : 'height:100%;'"
      >
        <div :class="{ mobileDisplayContainer: isMobileSized }">
          <StepIcons :currentView="'launch'" :isMobileSized="isMobileSized" />
          <v-row no-gutters align="center" :style="isMobileSized ? '' : 'height:75vh'">
            <div>
              <div class="px-8">
                <div
                  class="text-h4 font-weight-bold confirmationTitle"
                  :class="{ mobileTitle: isMobileSized }"
                >
                  You're Ready to Start Serving Ads!
                </div>
                <p
                  v-if="isQuickStart"
                  class="largeDescriptionText"
                  :class="isMobileSized ? 'mobileDescriptionText mt-2' : 'mt-6'"
                >
                  Click the Serve My Ads button to start serving your ads on the web!
                </p>
                <p
                  v-else
                  class="largeDescriptionText"
                  :class="isMobileSized ? 'mobileDescriptionText mt-2' : 'mt-6'"
                >
                  Create an account with us and click the Serve My Ads button to start serving your
                  free ads on the web!
                </p>
              </div>
              <div class="adsList px-5" :class="{ mobileAdsList: isMobileSized }">
                <div
                  v-for="(storyboard, idx) in storyboards"
                  :key="'storyboard' + idx"
                  style="float: left; position: relative;"
                  class="ml-3 mb-3"
                >
                  <!-- for mobile, only show the 300x250 preview -->
                  <StoryboardContainer
                    v-if="!isMobileSized || storyboard.width == 300"
                    :index="idx"
                    :key="`sb-container-${idx}`"
                    :template="storyboard.templates[0]"
                    :inventories="inventoryItems"
                    :shouldDisplayWatermark="isFreemium"
                  />
                </div>
              </div>
              <div
                style=""
                class="backButtonDisplay"
                :class="{ mobileBackButtonDisplay: isMobileSized }"
              >
                <v-btn
                  class="mr-3"
                  dark
                  color="grey lighten-1"
                  @click="
                    guibus_publish('dispatch:beacon', {
                      eventId: `welcome-launch_slide_back_button-click${
                        isMobileSized ? '-mobile' : ''
                      }`,
                    })
                    $emit('stepBack', 'advertiser')
                  "
                  >Back</v-btn
                >
              </div>
            </div>
          </v-row>
        </div>
      </v-col>
      <v-col
        class="d-flex align-center justify-center confirmationScroll"
        :class="isMobileSized ? '' : 'pl-7 pr-10'"
        style="background-color: rgb(94, 0, 235); position:relative;"
        :style="isMobileSized ? 'padding-bottom: 166px;' : 'height:100%;'"
      >
        <div>
          <div
            v-if="!loadingPage && !hasValidPaymentInfo && isAuthenticated"
            class="billingFormContainer"
          >
            <BillingForm
              :parentFemsName="'quickStart'"
              :advertiserData="advertiserData"
              @handleBillingInfoSave="handleBillingInfoSave"
              @setSystemMessage="$emit('setSystemMessage', $event)"
            />
          </div>
          <SignupForm
            v-else-if="!loadingPage && !isAuthenticated"
            :isAgency="isAgency"
            :advertiserData="advertiserData"
            :isMobileSized="isMobileSized"
            @userHasSignedUp="userHasSignedUp"
            @setSystemMessage="$emit('setSystemMessage', $event)"
          />
          <div class="text-center mt-4">
            <v-tooltip top max-width="300" :disabled="mode !== 'dynamic' || !!inventoryItemsProps">
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-btn
                    :disabled="serveMyAdsButtonIsDisabled"
                    class="white--text"
                    color="orange"
                    x-large
                    @click="serveAds"
                    >Serve My Ads</v-btn
                  >
                </div>
              </template>
              <span
                >In order to serve product catalog ads, you must have a product feed configured. You
                can do this by clicking "Back" to the "Your Ads" page and clicking "Import
                Products". If you wish to import your products later, just click "Save and Continue
                Later"</span
              >
            </v-tooltip>
            <div
              class="continueLaterOption caption mt-10"
              :class="saveAndContinueButtonIsDisabled ? 'disabledText' : ''"
              @click="saveAndContinueLater"
              ><i>Save and Continue Later</i></div
            >
            <div>or</div>
            <div
              class="thirdPartyOption caption"
              :class="thirdPartyButtonIsDisabled ? 'disabledText' : ''"
              @click="runVia3rdPartyDSP"
              ><i>Click here to run these ads via a third party DSP</i></div
            >
          </div>
        </div>
      </v-col>
    </v-row>
    <AdApprovalMessaging
      :dialogIsOpen="showAdApprovalModal"
      @approvalAcknowledged="handleAcknowledgement"
    />
  </div>
</template>

<script>
import StepIcons from '../units/StepIcons.vue'
import BillingForm from '../units/BillingForm.vue'
import SignupForm from '../units/SignupForm.vue'
import StoryboardContainer from '../units/StoryboardContainer'
import AdApprovalMessaging from 'component-ad-approval-messaging'
import AdApprovalMessagingTimeLogic from 'component-ad-approval-messaging/timeLogic.js'
import CampaignSolutionInterfaceFactoryNoAuth from 'bs-interface-campaign-solution/bs-interface-campaign-solution-prelogin'

export default {
  components: {
    StepIcons,
    BillingForm,
    AdApprovalMessaging,
    SignupForm,
    StoryboardContainer,
  },
  props: {
    vertical: {
      type: String,
      default: 'retail',
    },
    subVertical: {
      type: String,
      default: 'Legal',
    },
    mode: {
      type: String,
      default: 'static',
    },
    budgetingSchedule: {
      type: Object,
      default: undefined,
    },
    inventoryItemsProps: {
      type: Array,
      default: undefined,
    },
    storyboardsProps: {
      type: Array,
      default: undefined,
    },
    isAgency: {
      type: Boolean,
      default: false,
    },
    isQuickStart: {
      type: Boolean,
      default: false,
    },
    advertiserData: {
      type: Object,
      default: undefined,
    },
    isMobileSized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      solutionThumbnails: [],
      solutionOptionsForVertical: [],
      hasValidPaymentInfo: false,
      showAdApprovalModal: false,
      adApprovalAcknowledgementResolve: undefined,
      loadingPage: true,
      budgetingAndSchedulingSettings: this.budgetingSchedule,
      isAuthenticated: false,
      userCompletedSignup: false,
      storyboards: this.storyboardsProps || [],
      inventoryItems: this.inventoryItemsProps,
      isFreemium: !this.$firebaseUser,
    }
  },
  async beforeMount() {
    if (this.isQuickStart) {
      this.isAuthenticated = true
      await this.validateBilling()
    }
    this.loadingPage = false
  },
  methods: {
    handleBillingInfoSave() {
      this.validateBilling()
    },
    userHasSignedUp() {
      this.userCompletedSignup = true
    },
    handleAcknowledgement() {
      this.showAdApprovalModal = false
      this.adApprovalAcknowledgementResolve()
    },
    async serveAds() {
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-launch_slide_serve_ads_button-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      await this.makeChecksBeforeLaunch()
      this.$emit('save', {
        isActivatingMediaChannel: true,
        isAdTagsUser: false,
        hasValidPaymentInfo: this.hasValidPaymentInfo,
        isOnboarding: this.isOnboarding,
      })
    },
    saveAndContinueLater() {
      // shouldn't be able to reach this if the user hasn't signed up, but check in case
      if (this.saveAndContinueButtonIsDisabled) {
        return
      }
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-launch_slide_save_and_continue_later_link-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      this.$emit('save', {
        isActivatingMediaChannel: false,
        isAdTagsUser: false,
        hasValidPaymentInfo: this.hasValidPaymentInfo,
        isOnboarding: this.isOnboarding,
      })
    },
    runVia3rdPartyDSP() {
      // shouldn't be able to reach this if the user hasn't signed up, but check in case
      if (this.thirdPartyButtonIsDisabled) {
        return
      }
      this.guibus_publish('dispatch:beacon', {
        eventId: `welcome-launch_slide_run_via_third_party_link-click${
          this.isMobileSized ? '-mobile' : ''
        }`,
      })
      this.$emit('save', {
        isActivatingMediaChannel: false,
        isAdTagsUser: true,
        hasValidPaymentInfo: this.hasValidPaymentInfo,
        isOnboarding: this.isOnboarding,
      })
    },
    async makeChecksBeforeLaunch() {
      // shouldn't be able to reach this if the user hasn't signed up, but check in case
      if (!this.userCompletedSignup) {
        return
      }
      // only bother validating billing during quick start, not onboarding
      if (!this.isOnboarding) {
        await this.validateBilling()
      }
      if (
        AdApprovalMessagingTimeLogic.AdsAreScheduledToRunBeforeMinimumApprovalWindow(
          this.budgetingAndSchedulingSettings
        )
      ) {
        this.showAdApprovalModal = true
        await new Promise(resolve => {
          this.adApprovalAcknowledgementResolve = resolve
        })
      }
    },
    async validateBilling() {
      await this.BillingAccountInterface.getHasValidPaymentInfo(response => {
        this.hasValidPaymentInfo = response
      })
    },
  },
  computed: {
    CampaignSolutionInterface() {
      return window.adacado.bscs
        ? window.adacado.bscs.CampaignSolutionInterfaceFactory()
        : CampaignSolutionInterfaceFactoryNoAuth()
    },
    BillingAccountInterface() {
      return window.adacado.bscs ? window.adacado.bscs.BillingAccountInterfaceFactory() : undefined
    },
    // the buttons should be disabled:
    //  - during on-boarding, all 3 buttons, if the user hasn't signed up yet
    //  - during quickstart, if the user doesn't have valid payment info, then only the "serve my ads" button should be disabled
    serveMyAdsButtonIsDisabled() {
      if (this.mode === 'dynamic' && !this.inventoryItemsProps) {
        return true
      }
      return this.isOnboarding ? !this.userCompletedSignup : !this.hasValidPaymentInfo
    },
    saveAndContinueButtonIsDisabled() {
      return this.isOnboarding && !this.userCompletedSignup
    },
    thirdPartyButtonIsDisabled() {
      return this.isOnboarding && !this.userCompletedSignup
    },
    isOnboarding() {
      return !window.location.href.includes('/mo/quickstart')
    },
  },
}
</script>

<style lang="scss" scoped>
.confirmationContainer {
  width: 100vw;
  height: 100%;
  .confirmationContent {
    display: flex;
    height: 100%;
    position: relative;
    width: 100%;
    margin: auto;
    align-items: center;
    .billingFormContainer {
      height: calc(100vh - 244px);
      max-height: 602px;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 12px;
        height: 12px;
      }
      &::-webkit-scrollbar-track {
        background: #e6e6e6;
        border-left: 1px solid #dadada;
      }
      &::-webkit-scrollbar-thumb {
        background: #b0b0b0;
        border: solid 3px #e6e6e6;
        border-radius: 7px;
      }
    }
    &.mobileConfirmationContent {
      height: unset;
    }
  }
}
.continueLaterOption {
  cursor: pointer;
  color: white;
  &.disabledText {
    color: #b0b0b0;
    cursor: not-allowed;
  }
}
.mobileDisplayContainer {
  position: relative;
}
.confirmationTitle {
  &.mobileTitle {
    font-size: 1.5rem !important;
  }
}
.thirdPartyOption {
  cursor: pointer;
  color: white;
  &.disabledText {
    color: #b0b0b0;
    cursor: not-allowed;
  }
}
.adsList {
  width: 50vw;
  height: 60vh;
  overflow: auto;
  &.mobileAdsList {
    width: 100vw;
    height: fit-content;
  }
}
.backButtonDisplay {
  position: absolute;
  bottom: 50px;
  right: 25px;
  &.mobileBackButtonDisplay {
    position: fixed;
    bottom: 90px;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    z-index: 10;
  }
}
.adsList,
.adsText {
  &::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #e6e6e6;
    border-left: 1px solid #dadada;
  }

  &::-webkit-scrollbar-thumb {
    background: #b0b0b0;
    border: solid 3px #e6e6e6;
    border-radius: 7px;
  }
}
@media (min-height: 600px) and (max-height: 700px) {
  .adsList {
    height: 43vh;
  }
}
@media (min-height: 700px) and (max-height: 850px) {
  .adsList {
    height: 50vh;
  }
}
</style>
